/**
 * Nakama match data opcodes.
 *
 * Client -> server occupies 1..99, server -> client occupies 100+. Opcode
 * numbers are permanent: retire one rather than reuse it for a new meaning.
 *
 * Declared as frozen objects rather than TS `enum`s because `isolatedModules`
 * and the goja bundle both prefer plain values.
 */
export const ClientOpCode = {
    /** A game move, validated by the engine. Carries the sender's personal `v`. */
    MOVE: 1,
    /** Asks for a full snapshot of both surfaces. Sent on reconnect. */
    SYNC_REQUEST: 2,
    /**
     * Match-level control from the host: start, pause, remove a seat, set the
     * turn limit. These are NOT engine moves - they never reach the reducer.
     */
    HOST_ACTION: 3,
    /**
     * Something said at the table: one of the canned phrases, or typed words.
     *
     * A **phrase** travels as an id and the server maps it to words from its own
     * config, so nothing a client invents can appear on another screen as a
     * phrase. That much has always been true and still is.
     *
     * **Typed words** travel as text, and are a separate path with its own
     * limits: tidied and measured by the protocol, rate-limited by the server,
     * and switchable off by the host. This comment used to say there was no
     * free-text path at all; there is one now, and it is limited rather than
     * absent.
     */
    CHAT: 4,
    /**
     * WebRTC signalling, addressed to one other person in the match.
     *
     * An opaque blob - an SDP offer or answer, or an ICE candidate - that the
     * server forwards without reading. It checks who is sending, who is meant to
     * receive it, and how big it is, and nothing else: parsing SDP would make
     * the server responsible for a format it has no business understanding, and
     * every bug in that parser would sit in a security boundary.
     *
     * The audio itself never comes near here. This is only how two browsers find
     * each other.
     */
    VOICE: 5,
    /**
     * Whether this person is in the conversation, and what they have muted.
     *
     * Separate from VOICE because it is a different kind of thing: signalling is
     * addressed to one peer and never read by the server, while this is table
     * state everybody can see. Muting happens locally and instantly - the server
     * is told so it can tell everybody ELSE, not so it can do anything.
     *
     * The whole state travels every time rather than a change to it. Setting is
     * idempotent and cannot arrive out of order; incrementing is neither.
     */
    VOICE_STATE: 6,
    /**
     * Which face this person is wearing AT THIS TABLE (D92).
     *
     * Separate from the profile on purpose, and that separation is the whole
     * feature: a guest may wear one of the host's themed faces for the length of
     * the game and does not keep it. Writing it to the profile would be keeping
     * it, and one friend with a pack would outfit the group for ever.
     *
     * Shaped like VOICE_STATE rather than like a move: the whole choice travels
     * every time, so it is idempotent and cannot arrive out of order.
     */
    WEAR: 7,
    /**
     * "What I am wearing has changed - look again."
     *
     * Equipping happens over an RPC, outside the match, and the match caches
     * what each person is wearing when they JOIN so the loop never touches
     * storage. Without this, a host who changed their deck mid-game repainted
     * nothing until they rejoined - the change landed on the server and stopped
     * there.
     *
     * Carries nothing. The server re-reads what it already knows how to read,
     * which is also what makes it safe: a client cannot say what it is wearing,
     * only that it has changed.
     */
    LOOK: 8,
};
export const ServerOpCode = {
    VIEW: 100,
    ERROR: 101,
    // 102 was SEATS in the original plan. The seat roster is public and identical
    // for everyone, so it lives in the table section of VIEW instead - one
    // message, no chance of the roster and the board disagreeing. Not reused.
    GAME_OVER: 103,
    /** A signalling blob from another person in the match, forwarded verbatim. */
    VOICE: 104,
};
//# sourceMappingURL=opcodes.js.map